import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  Flag, 
  Eye, 
  EyeOff, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Clock,
  User,
  MessageSquare,
  ArrowLeft,
  RefreshCw
} from "lucide-react";
import { Link } from "react-router-dom";

const REASON_LABELS = {
  spam: { label: "Spam", color: "bg-gray-500" },
  harassment: { label: "Harassment", color: "bg-red-500" },
  inappropriate: { label: "Inappropriate", color: "bg-orange-500" },
  misinformation: { label: "Misinformation", color: "bg-yellow-500" },
  other: { label: "Other", color: "bg-blue-500" }
};

const STATUS_BADGES = {
  pending: { label: "Pending", variant: "outline", icon: Clock },
  reviewed: { label: "Reviewed", variant: "default", icon: CheckCircle },
  dismissed: { label: "Dismissed", variant: "secondary", icon: XCircle }
};

export default function AdminCommunity() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ pending: 0, reviewed: 0, dismissed: 0 });
  const [activeTab, setActiveTab] = useState("pending");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const fetchReports = useCallback(async (statusFilter = "all", pageNum = 1, append = false) => {
    try {
      if (!append) setLoading(true);
      
      const response = await axios.get(`${API}/admin/community/reports`, {
        params: { 
          page: pageNum, 
          limit: 20,
          status: statusFilter === "all" ? "all" : statusFilter
        },
        withCredentials: true
      });
      
      if (append) {
        setReports(prev => [...prev, ...(response.data.reports || [])]);
      } else {
        setReports(response.data.reports || []);
      }
      setHasMore(response.data.has_more || false);
      setStats(response.data.stats || { pending: 0, reviewed: 0, dismissed: 0 });
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error("Failed to load reports");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchReports(activeTab === "all" ? "all" : activeTab);
  }, [fetchReports, activeTab]);

  const handleTabChange = (value) => {
    setActiveTab(value);
    setPage(1);
    fetchReports(value === "all" ? "all" : value, 1);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    setPage(1);
    fetchReports(activeTab === "all" ? "all" : activeTab, 1);
  };

  const handleToggleVisibility = async (postId, currentlyHidden) => {
    try {
      await axios.put(
        `${API}/admin/community/posts/${postId}/visibility`,
        null,
        { 
          params: { hidden: !currentlyHidden },
          withCredentials: true 
        }
      );
      
      // Update local state
      setReports(prev => prev.map(r => 
        r.post_id === postId 
          ? { ...r, post_is_hidden: !currentlyHidden }
          : r
      ));
      
      toast.success(currentlyHidden ? "Post restored" : "Post hidden");
    } catch {
      toast.error("Failed to update post visibility");
    }
  };

  const handleUpdateStatus = async (reportId, newStatus) => {
    try {
      await axios.put(
        `${API}/admin/community/reports/${reportId}/status`,
        null,
        { 
          params: { status: newStatus },
          withCredentials: true 
        }
      );
      
      // Update local state
      setReports(prev => prev.map(r => 
        r.report_id === reportId 
          ? { ...r, status: newStatus }
          : r
      ));
      
      // Update stats
      setStats(prev => {
        const oldStatus = reports.find(r => r.report_id === reportId)?.status || "pending";
        return {
          ...prev,
          [oldStatus]: Math.max(0, prev[oldStatus] - 1),
          [newStatus]: prev[newStatus] + 1
        };
      });
      
      toast.success(`Report marked as ${newStatus}`);
    } catch {
      toast.error("Failed to update report status");
    }
  };

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchReports(activeTab === "all" ? "all" : activeTab, nextPage, true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f5f9f5] to-[#e8f0e8] p-4 md:p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="p-2 hover:bg-white/50 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5 text-[#5a8a5a]" />
            </Link>
            <div>
              <h1 className="text-2xl font-heading text-[#5a8a5a]">Community Moderation</h1>
              <p className="text-sm text-[#a69373]/60">Review reported posts and manage content</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-reports"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="glass-card">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#a69373]">{stats.pending}</p>
                <p className="text-xs text-[#a69373]/60">Pending Review</p>
              </div>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#a69373]">{stats.reviewed}</p>
                <p className="text-xs text-[#a69373]/60">Reviewed</p>
              </div>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <XCircle className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#a69373]">{stats.dismissed}</p>
                <p className="text-xs text-[#a69373]/60">Dismissed</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Reports Table */}
        <Card className="glass-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading text-[#5a8a5a] flex items-center gap-2">
              <Flag className="w-5 h-5" />
              Reported Posts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
              <TabsList className="mb-4">
                <TabsTrigger value="pending" data-testid="tab-pending">
                  Pending ({stats.pending})
                </TabsTrigger>
                <TabsTrigger value="reviewed" data-testid="tab-reviewed">
                  Reviewed ({stats.reviewed})
                </TabsTrigger>
                <TabsTrigger value="dismissed" data-testid="tab-dismissed">
                  Dismissed ({stats.dismissed})
                </TabsTrigger>
                <TabsTrigger value="all" data-testid="tab-all">
                  All
                </TabsTrigger>
              </TabsList>

              <TabsContent value={activeTab} className="mt-0">
                {loading ? (
                  <div className="space-y-4">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="animate-pulse p-4 bg-gray-50 rounded-lg">
                        <div className="h-4 bg-gray-200 rounded w-1/4 mb-2" />
                        <div className="h-3 bg-gray-200 rounded w-full mb-2" />
                        <div className="h-3 bg-gray-200 rounded w-3/4" />
                      </div>
                    ))}
                  </div>
                ) : reports.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle className="w-12 h-12 text-green-300 mx-auto mb-4" />
                    <p className="text-[#a69373]/60">No reports to show</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {reports.map((report) => (
                      <ReportCard
                        key={report.report_id}
                        report={report}
                        onToggleVisibility={handleToggleVisibility}
                        onUpdateStatus={handleUpdateStatus}
                        formatDate={formatDate}
                      />
                    ))}
                    
                    {hasMore && (
                      <Button 
                        variant="outline" 
                        className="w-full mt-4"
                        onClick={loadMore}
                        data-testid="load-more-reports"
                      >
                        Load More
                      </Button>
                    )}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ReportCard({ report, onToggleVisibility, onUpdateStatus, formatDate }) {
  const reasonInfo = REASON_LABELS[report.reason] || REASON_LABELS.other;
  const statusInfo = STATUS_BADGES[report.status] || STATUS_BADGES.pending;
  const StatusIcon = statusInfo.icon;

  return (
    <div 
      className={`p-4 rounded-lg border ${report.post_is_hidden ? "bg-red-50/50 border-red-200" : "bg-white border-gray-200"}`}
      data-testid={`report-${report.report_id}`}
    >
      {/* Report Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <Badge className={`${reasonInfo.color} text-white`}>
            {reasonInfo.label}
          </Badge>
          <Badge variant={statusInfo.variant} className="flex items-center gap-1">
            <StatusIcon className="w-3 h-3" />
            {statusInfo.label}
          </Badge>
          {report.post_is_hidden && (
            <Badge variant="destructive" className="flex items-center gap-1">
              <EyeOff className="w-3 h-3" />
              Hidden
            </Badge>
          )}
        </div>
        <span className="text-xs text-[#a69373]/50">{formatDate(report.created_at)}</span>
      </div>

      {/* Post Content */}
      <div className="bg-gray-50 rounded-lg p-3 mb-3">
        <div className="flex items-center gap-2 mb-2">
          <User className="w-4 h-4 text-[#a69373]/50" />
          <span className="text-sm font-medium text-[#a69373]">{report.post_author || "Anonymous"}</span>
          <span className="text-xs text-[#a69373]/40">• {formatDate(report.post_created_at)}</span>
        </div>
        <p className="text-sm text-[#a69373]/80 whitespace-pre-wrap">
          {report.post_content || "Post content unavailable"}
        </p>
        {report.post_report_count > 1 && (
          <div className="mt-2 flex items-center gap-1 text-xs text-amber-600">
            <AlertTriangle className="w-3 h-3" />
            <span>{report.post_report_count} total reports on this post</span>
          </div>
        )}
      </div>

      {/* Report Details */}
      {report.details && (
        <div className="bg-amber-50 rounded-lg p-3 mb-3">
          <div className="flex items-center gap-2 mb-1">
            <MessageSquare className="w-4 h-4 text-amber-600" />
            <span className="text-xs font-medium text-amber-700">Reporter&apos;s Note:</span>
          </div>
          <p className="text-sm text-amber-800">{report.details}</p>
        </div>
      )}

      {/* Reporter Info */}
      <div className="text-xs text-[#a69373]/50 mb-3">
        Reported by: {report.reporter_name || "Anonymous"}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onToggleVisibility(report.post_id, report.post_is_hidden)}
          data-testid={`toggle-visibility-${report.report_id}`}
        >
          {report.post_is_hidden ? (
            <>
              <Eye className="w-4 h-4 mr-1" />
              Restore Post
            </>
          ) : (
            <>
              <EyeOff className="w-4 h-4 mr-1" />
              Hide Post
            </>
          )}
        </Button>
        
        {report.status === "pending" && (
          <>
            <Button
              variant="default"
              size="sm"
              onClick={() => onUpdateStatus(report.report_id, "reviewed")}
              className="bg-green-600 hover:bg-green-700"
              data-testid={`mark-reviewed-${report.report_id}`}
            >
              <CheckCircle className="w-4 h-4 mr-1" />
              Mark Reviewed
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onUpdateStatus(report.report_id, "dismissed")}
              data-testid={`dismiss-${report.report_id}`}
            >
              <XCircle className="w-4 h-4 mr-1" />
              Dismiss
            </Button>
          </>
        )}
        
        {report.status !== "pending" && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onUpdateStatus(report.report_id, "pending")}
            data-testid={`reopen-${report.report_id}`}
          >
            <Clock className="w-4 h-4 mr-1" />
            Reopen
          </Button>
        )}
      </div>
    </div>
  );
}
